import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../lib/ApiProvider';
import type {
  MarketDataCreateInput,
  MarketDataListParams,
  MarketDataUpdateInput,
} from '../../lib/api';

/** Dossier-scoped query keys for LOT 8C market data (REQ-F-061). */
export const marketDataKeys = {
  all: ['market-data'] as const,
  dossier: (dossierId: string) => ['market-data', dossierId] as const,
  list: (dossierId: string, params: MarketDataListParams) =>
    ['market-data', dossierId, 'list', params] as const,
};

export function useMarketData(
  dossierId: string,
  params: MarketDataListParams = {},
  enabled = true,
) {
  const api = useApi();
  return useQuery({
    queryKey: marketDataKeys.list(dossierId, params),
    queryFn: () => api.listMarketData(dossierId, params),
    enabled,
  });
}

export function useCreateMarketData(dossierId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MarketDataCreateInput) => api.createMarketData(dossierId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: marketDataKeys.dossier(dossierId) }),
  });
}

export function useUpdateMarketData(dossierId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: MarketDataUpdateInput }) =>
      api.updateMarketData(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: marketDataKeys.dossier(dossierId) }),
  });
}

export function useDeleteMarketData(dossierId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteMarketData(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: marketDataKeys.dossier(dossierId) }),
  });
}
